import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";
import { Field, Msg, useFlash } from "../ui";
import { API_URL } from "../auth";
import { relTime } from "../api";
import { useMe } from "./AdminShell";
import { AppLegalDocumentsCard } from "./AppLegalDocumentsCard";
import { LegalDocumentsCard } from "./LegalDocumentsCard";
import { SentryDevToolsCard } from "../components/SentryDevToolsCard";

interface Church {
  id: string;
  name: string;
  slug: string;
  status: string;
  timezone: string;
  defaultLocale: "de" | "en";
  celebrationUrl: string | null;
  celebrationTitle: string | null;
  celebrationDate: string | null;
  celebrationVideoId: string | null;
  teamUrl: string | null;
  calendlyUrl: string | null;
  donateUrl: string | null;
  churchCenterUrl: string | null;
  crisisContactUserId: string | null;
  decisionContactUserId: string | null;
}
interface Staff {
  userId: string;
  role: string;
  active: boolean;
  alias: string;
  fullName: string | null;
}

export function AdminSettings() {
  const { t } = useT();
  const scope = useScope();
  const me = useMe();
  const q = useQuery({ queryKey: ["admin", "church", scope.churchId], queryFn: () => api<{ church: Church | null; staff: Staff[] }>(`/admin/church${scopeQuery(scope, "church")}`) });

  if (q.isLoading) return <p className="muted">{t("admin.common.loading")}</p>;
  const canEdit = Boolean(me.data?.isSuperadmin || me.data?.membership?.role === "admin");
  const isSuper = Boolean(me.data?.isSuperadmin);

  if (!q.data?.church) {
    return (
      <>
        <h1>{t("admin.settings.title")}</h1>
        {isSuper ? (
          <>
            <p className="muted">{t("admin.settings.globalScopeHint")}</p>
            <AppLegalDocumentsCard canEdit={isSuper} />
            <SentryDevToolsCard />
          </>
        ) : (
          <p className="muted">{t("admin.settings.noChurch")}</p>
        )}
      </>
    );
  }

  return (
    <>
      <p className="eyebrow">{q.data.church.name}</p>
      <h1>{t("admin.settings.title")}</h1>
      {!canEdit ? <p className="muted">{t("admin.settings.adminsOnly")}</p> : null}
      {isSuper ? <AppLegalDocumentsCard canEdit={isSuper} /> : null}
      <SettingsForm key={q.data.church.id} church={q.data.church} staff={q.data.staff} canEdit={canEdit} />
      <LegalDocumentsCard canEdit={canEdit} churchName={q.data.church.name} />
      <SentryDevToolsCard />
      <AuditCard />
    </>
  );
}

function SettingsForm({ church, staff, canEdit }: { church: Church; staff: Staff[]; canEdit: boolean }) {
  const { t } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const [f, setF] = useState(church);
  const [flash, run, busy] = useFlash();
  useEffect(() => setF(church), [church]);
  const set = (k: keyof Church) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value || null });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = {
      name: f.name,
      timezone: f.timezone,
      defaultLocale: f.defaultLocale,
      celebrationUrl: f.celebrationUrl || null,
      celebrationTitle: f.celebrationTitle || null,
      celebrationDate: f.celebrationDate ? new Date(f.celebrationDate).toISOString() : null,
      teamUrl: f.teamUrl || null,
      calendlyUrl: f.calendlyUrl || null,
      donateUrl: f.donateUrl || null,
      churchCenterUrl: f.churchCenterUrl || null,
      crisisContactUserId: f.crisisContactUserId || null,
      decisionContactUserId: f.decisionContactUserId || null,
    };
    if (await run(() => api(`/admin/church${scopeQuery(scope, "church")}`, { method: "PATCH", json: body }), t("admin.common.saved"))) void qc.invalidateQueries({ queryKey: ["admin", "church"] });
  };
  const contactName = (s: Staff) => `${s.fullName ?? s.alias} (${s.role})`;
  const videoId = f.celebrationUrl ? (f.celebrationUrl.match(/(?:v=|youtu\.be\/|embed\/|live\/)([A-Za-z0-9_-]{11})/)?.[1] ?? null) : null;

  return (
    <form className="form settings" onSubmit={save}>
      <fieldset disabled={!canEdit}>
        <div className="card">
          <h2>{t("admin.settings.church")}</h2>
          <div className="two-col">
            <Field label={t("admin.settings.name")}>
              <input required value={f.name} onChange={set("name")} />
            </Field>
            <Field label={t("admin.settings.timezone")}>
              <input value={f.timezone} onChange={set("timezone")} />
            </Field>
            <Field label={t("admin.settings.defaultLocale")}>
              <select value={f.defaultLocale} onChange={set("defaultLocale")}>
                <option value="de">{t("admin.common.de")}</option>
                <option value="en">{t("admin.common.en")}</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="card">
          <h2>{t("admin.settings.celebration")}</h2>
          <div className="two-col">
            <Field label={t("admin.settings.celebrationUrl")}>
              <input type="url" value={f.celebrationUrl ?? ""} onChange={set("celebrationUrl")} />
            </Field>
            <Field label={t("admin.settings.celebrationTitle")}>
              <input value={f.celebrationTitle ?? ""} onChange={set("celebrationTitle")} />
            </Field>
            <Field label={t("admin.settings.celebrationDate")}>
              <input type="date" value={f.celebrationDate ? f.celebrationDate.slice(0, 10) : ""} onChange={set("celebrationDate")} />
            </Field>
          </div>
          {videoId ? (
            <div className="preview">
              <span className="eyebrow">{t("admin.settings.preview")}</span>
              <img src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`} alt="" width={240} height={135} />
            </div>
          ) : null}
        </div>

        <div className="card">
          <h2>{t("admin.settings.links")}</h2>
          <div className="two-col">
            <Field label={t("admin.settings.teamUrl")}>
              <input type="url" value={f.teamUrl ?? ""} onChange={set("teamUrl")} />
            </Field>
            <Field label={t("admin.settings.calendlyUrl")}>
              <input type="url" value={f.calendlyUrl ?? ""} onChange={set("calendlyUrl")} />
            </Field>
            <Field label={t("admin.settings.donateUrl")}>
              <input type="url" value={f.donateUrl ?? ""} onChange={set("donateUrl")} />
            </Field>
            <Field label={t("admin.settings.churchCenterUrl")}>
              <input type="url" value={f.churchCenterUrl ?? ""} onChange={set("churchCenterUrl")} />
            </Field>
          </div>
        </div>

        <div className="card">
          <h2>{t("admin.settings.contacts")}</h2>
          <div className="two-col">
            <Field label={t("admin.settings.crisisContact")}>
              <select value={f.crisisContactUserId ?? ""} onChange={set("crisisContactUserId")}>
                <option value="">{t("admin.settings.none")}</option>
                {staff
                  .filter((s) => s.role !== "leader")
                  .map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {contactName(s)}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={t("admin.settings.decisionContact")}>
              <select value={f.decisionContactUserId ?? ""} onChange={set("decisionContactUserId")}>
                <option value="">{t("admin.settings.none")}</option>
                {staff
                  .filter((s) => s.role !== "leader")
                  .map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {contactName(s)}
                    </option>
                  ))}
              </select>
            </Field>
          </div>
        </div>

        <div className="card">
          <h2>{t("admin.settings.staff")}</h2>
          <div className="list">
            {staff.map((s) => (
              <div key={s.userId} className="listrow">
                <div className="grow">
                  <strong>{s.fullName ?? s.alias}</strong>
                </div>
                <span className="pill">{s.role}</span>
                {!s.active ? <span className="pill warn">{t("admin.settings.inactive")}</span> : null}
              </div>
            ))}
          </div>
        </div>

        {canEdit ? (
          <div className="actions">
            <button className="btn" disabled={busy}>
              {t("admin.common.save")}
            </button>
            <Msg msg={flash} />
          </div>
        ) : null}
      </fieldset>
    </form>
  );
}

interface AuditEntry {
  id: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  actor: string;
  createdAt: string;
}

/** Audit log (FR-ADM-10): readable list plus a Word export; the API records every admin write. */
function AuditCard() {
  const { t, locale } = useT();
  const scope = useScope();
  const sq = scopeQuery(scope, "church", { limit: "50" });
  const q = useQuery({ queryKey: ["admin", "audit", scope.churchId], queryFn: () => api<{ entries: AuditEntry[] }>(`/admin/audit${sq}`) });
  return (
    <div className="form settings">
      <div className="card">
        <div className="toolbar">
          <h2>{t("admin.settings.audit")}</h2>
          <a className="btn small ghost" style={{ marginInlineStart: "auto" }} href={`${API_URL}/admin/audit/export${scopeQuery(scope, "church")}`} target="_blank" rel="noreferrer">
            {t("admin.settings.auditExport")}
          </a>
        </div>
        <p className="muted small">{t("admin.settings.auditBody")}</p>
        {q.data?.entries.length === 0 ? <p className="muted">{t("admin.settings.auditEmpty")}</p> : null}
        <div className="table-wrap">
          <table className="people compact">
            <thead>
              <tr>
                <th>{t("admin.settings.auditTime")}</th>
                <th>{t("admin.settings.auditActor")}</th>
                <th>{t("admin.settings.auditAction")}</th>
                <th>{t("admin.settings.auditTarget")}</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.entries.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap">{relTime(e.createdAt, locale)}</td>
                  <td>{e.actor}</td>
                  <td>
                    <code>{e.action}</code>
                  </td>
                  <td className="muted small">{e.targetType ? `${e.targetType} ${e.targetId?.slice(0, 8) ?? ""}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
